import React, { useEffect, useRef, useState, useCallback } from 'react';
import './ProjectTracker.css';
import api from '../services/api';

const CHAT_API_BASE = '/api/chat';

async function authFetch(url, opts = {}) {
  const token = await api.ensureAuthenticated();
  return fetch(url, {
    ...opts,
    headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}` },
  });
}

// Full logistics status set (shared by PO and item lines)
const STATUS_OPTIONS = [
  '', 'Waiting on Acknowledgment', 'Ordered', 'Preparing to Ship',
  'Shipped', 'In Transit', 'Out for Delivery', 'Delivered', 'Installing',
  'Delayed', 'Backordered', 'Partial Delivery', 'Damaged', 'Returned', 'Exception',
];
const CARRIER_OPTIONS = ['', 'USPS', 'UPS', 'FedEx', 'DHL', 'OnTrac', 'Freight', 'Other'];

// Tracking fields rendered on every line (PO header + each item)
const FIELDS = [
  { key: 'estimated_ship_date',     label: 'Est. Ship Date',    kind: 'date' },
  { key: 'tracking_number',         label: 'Tracking #',        kind: 'textarea' },
  { key: 'carrier',                 label: 'Carrier',           kind: 'select', options: CARRIER_OPTIONS },
  { key: 'delivery_status_updates', label: 'Delivery / Status', kind: 'select', options: STATUS_OPTIONS },
  { key: 'notes',                   label: 'Notes',             kind: 'textarea' },
];
const TRACK_KEYS = FIELDS.map(f => f.key);

// Auto-growing textarea so every text field word-wraps instead of scrolling sideways
const AutoTextarea = ({ value, onChange }) => {
  const ref = useRef(null);
  const resize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  };
  useEffect(() => { resize(); }, [value]);
  return (
    <textarea
      ref={ref}
      className="pt-input pt-wrap"
      rows={1}
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

const ProjectTracker = ({ projectId }) => {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [savingCount, setSavingCount] = useState(0); // in-flight saves
  const [pendingN, setPendingN] = useState(0);       // edits awaiting debounce
  const [showSaved, setShowSaved] = useState(false);
  const fileInputRef = useRef(null);

  // Per-item selection for the PDF download. Defaults to all items selected.
  const [selectedItems, setSelectedItems] = useState(() => new Set());
  const selInitRef = useRef(false);
  useEffect(() => {
    if (selInitRef.current) return;
    const ids = [];
    pos.forEach(po => (po.line_items || []).forEach(li => ids.push(li.id)));
    if (ids.length) { setSelectedItems(new Set(ids)); selInitRef.current = true; }
  }, [pos]);
  const uploadTargetRef = useRef(null); // PO id currently uploading to
  const posRef = useRef([]);            // latest pos for debounced/unmount saves
  const saveTimers = useRef({});        // poId -> debounce timeout
  const pendingRef = useRef(new Set()); // poIds awaiting save

  useEffect(() => { posRef.current = pos; }, [pos]);

  const fetchTracker = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const res = await authFetch(`${CHAT_API_BASE}/project-tracker.php?project_id=${projectId}`);
      const data = await res.json();
      if (data.success) {
        setPos(Array.isArray(data.data) ? data.data : []);
      } else {
        setErr(data.error || 'Failed to load tracker');
      }
    } catch (e) {
      setErr('Failed to load tracker');
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchTracker(); }, [fetchTracker]);

  const savePo = async (po) => {
    const header = {};
    TRACK_KEYS.forEach(k => { header[k] = po.attributes[k] ?? ''; });
    const items = po.line_items.map(li => {
      const o = { id: li.id };
      TRACK_KEYS.forEach(k => { o[k] = li[k] ?? ''; });
      return o;
    });
    const res = await authFetch(`${CHAT_API_BASE}/project-tracker.php`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ po_id: po.id, header, items }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Save failed');
  };

  // Persist one PO's tracking fields (reads the latest snapshot from posRef).
  const doSave = useCallback(async (poId) => {
    pendingRef.current.delete(poId);
    setPendingN(pendingRef.current.size);
    const po = posRef.current.find(p => p.id === poId);
    if (!po) return;
    setSavingCount(c => c + 1);
    try {
      await savePo(po);
      setErr(null);
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 1800);
    } catch (e) {
      setErr(e.message || 'Save failed');
    } finally {
      setSavingCount(c => c - 1);
    }
  }, []);

  // Debounced auto-save: coalesce rapid edits to a PO into one save ~800ms after the last keystroke.
  const scheduleSave = useCallback((poId) => {
    pendingRef.current.add(poId);
    setPendingN(pendingRef.current.size);
    if (saveTimers.current[poId]) clearTimeout(saveTimers.current[poId]);
    saveTimers.current[poId] = setTimeout(() => {
      delete saveTimers.current[poId];
      doSave(poId);
    }, 800);
  }, [doSave]);

  // On unmount (e.g. switching tabs), flush any pending edits so nothing is lost.
  useEffect(() => () => {
    Object.keys(saveTimers.current).forEach(poId => {
      clearTimeout(saveTimers.current[poId]);
      const po = posRef.current.find(p => p.id === poId);
      if (po) savePo(po).catch(() => {});
    });
  }, []);

  // Edit a PO-header tracking field. Per spec, the value is copied down to every line
  // item in that PO; item fields remain independently editable afterward.
  const updatePoField = (poId, key, value) => {
    setPos(prev => prev.map(po => {
      if (po.id !== poId) return po;
      return {
        ...po,
        attributes: { ...po.attributes, [key]: value },
        line_items: po.line_items.map(li => ({ ...li, [key]: value })),
      };
    }));
    scheduleSave(poId);
  };

  const updateItemField = (poId, itemId, key, value) => {
    setPos(prev => prev.map(po => {
      if (po.id !== poId) return po;
      return {
        ...po,
        line_items: po.line_items.map(li => li.id === itemId ? { ...li, [key]: value } : li),
      };
    }));
    scheduleSave(poId);
  };

  // ---- attachments ----
  const triggerUpload = (poId) => {
    uploadTargetRef.current = poId;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    const poId = uploadTargetRef.current;
    if (!file || !poId) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('purchase_order_id', poId);
      fd.append('name', file.name);
      const res = await authFetch(`${CHAT_API_BASE}/po-attachments.php`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) {
        setPos(prev => prev.map(po => po.id === poId
          ? { ...po, attachments: [...(po.attachments || []), data.data] }
          : po));
      } else {
        alert(data.error || 'Upload failed');
      }
    } catch (err) {
      alert('Upload failed');
    }
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      // Flush any pending edits and wait for them so the PDF reflects the latest data.
      const pending = Object.keys(saveTimers.current).map(poId => {
        clearTimeout(saveTimers.current[poId]);
        delete saveTimers.current[poId];
        const po = posRef.current.find(p => p.id === poId);
        return po ? savePo(po) : null;
      }).filter(Boolean);
      if (pending.length) {
        pendingRef.current.clear();
        setPendingN(0);
        await Promise.all(pending);
      }
      // Only the selected items go into the PDF (default is all selected).
      const itemParam = `&item_ids=${encodeURIComponent([...selectedItems].join(','))}`;
      const res = await authFetch(`${CHAT_API_BASE}/project-tracker-pdf.php?project_id=${projectId}${itemParam}`);
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Project_Tracker.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Failed to generate PDF: ' + e.message);
    }
    setDownloadingPdf(false);
  };

  const deleteAttachment = async (poId, attId) => {
    if (!window.confirm('Remove this attachment?')) return;
    try {
      const res = await authFetch(`${CHAT_API_BASE}/po-attachments.php?id=${attId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setPos(prev => prev.map(po => po.id === poId
          ? { ...po, attachments: (po.attachments || []).filter(a => a.id !== attId) }
          : po));
      }
    } catch (err) { /* ignore */ }
  };

  const renderField = (f, value, onChange) => {
    if (f.kind === 'date') {
      return <input className="pt-input" type="date" value={(value || '').slice(0, 10)}
                    onChange={(e) => onChange(e.target.value)} />;
    }
    if (f.kind === 'select') {
      return (
        <select className="pt-input" value={value || ''} onChange={(e) => onChange(e.target.value)}>
          {f.options.map(o => <option key={o} value={o}>{o || '—'}</option>)}
        </select>
      );
    }
    return <AutoTextarea value={value} onChange={onChange} />;
  };

  if (loading) return <div className="pt-state">Loading project tracker…</div>;
  if (err && pos.length === 0) return <div className="pt-state pt-error">{err}</div>;

  // Selection helpers (which line items are included in the PDF).
  const allItemIds = [];
  pos.forEach(po => (po.line_items || []).forEach(li => allItemIds.push(li.id)));
  const allSelected = allItemIds.length > 0 && allItemIds.every(id => selectedItems.has(id));
  const noneSelected = !allItemIds.some(id => selectedItems.has(id));
  const toggleItem = (id) => setSelectedItems(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = () => setSelectedItems(allSelected ? new Set() : new Set(allItemIds));
  const poItemIds = (po) => (po.line_items || []).map(li => li.id);
  const poAllSelected = (po) => { const ids = poItemIds(po); return ids.length > 0 && ids.every(id => selectedItems.has(id)); };
  const poSomeSelected = (po) => poItemIds(po).some(id => selectedItems.has(id));
  const togglePo = (po) => {
    const ids = poItemIds(po); const all = poAllSelected(po);
    setSelectedItems(prev => { const n = new Set(prev); ids.forEach(id => all ? n.delete(id) : n.add(id)); return n; });
  };

  return (
    <div className="project-tracker">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
        style={{ display: 'none' }}
        onChange={handleFileChosen}
      />

      <div className="pt-toolbar">
        <h2 className="pt-title">Project Tracker</h2>
        <div className="pt-toolbar-actions">
          {err ? (
            <span className="pt-error-inline">{err}</span>
          ) : (savingCount > 0 || pendingN > 0) ? (
            <span className="pt-status-msg pt-saving">Saving…</span>
          ) : showSaved ? (
            <span className="pt-status-msg pt-saved">All changes saved ✓</span>
          ) : (
            <span className="pt-status-msg pt-autosave">Auto-save on</span>
          )}
          <button
            className="pt-pdf-btn"
            onClick={handleDownloadPdf}
            disabled={downloadingPdf || pos.length === 0 || noneSelected}
            title={noneSelected ? 'Select at least one item to include in the PDF' : 'Download the selected items as a PDF'}
          >
            {downloadingPdf ? 'Preparing…' : '⬇ Download PDF'}
          </button>
        </div>
      </div>

      {pos.length === 0 ? (
        <div className="pt-state">No purchase orders for this project yet.</div>
      ) : (
        <div className="pt-table-wrap">
          <table className="pt-table">
            <thead>
              <tr>
                <th className="pt-col-sel" title="Include in PDF">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={el => { if (el) el.indeterminate = !allSelected && !noneSelected; }}
                    onChange={toggleAll}
                    aria-label="Select all items"
                  />
                </th>
                <th className="pt-col-line">PO / Item</th>
                {FIELDS.map(f => <th key={f.key}>{f.label}</th>)}
                <th className="pt-col-files">Packing Slips / Files</th>
              </tr>
            </thead>
            <tbody>
              {pos.map(po => {
                const a = po.attributes || {};
                const statusClass = `status-${(a.status || 'draft').toLowerCase().replace(/\s+/g, '-')}`;
                return (
                  <React.Fragment key={po.id}>
                    {/* PO header line */}
                    <tr className="pt-po-row">
                      <td className="pt-col-sel">
                        {(po.line_items || []).length > 0 && (
                          <input
                            type="checkbox"
                            checked={poAllSelected(po)}
                            ref={el => { if (el) el.indeterminate = poSomeSelected(po) && !poAllSelected(po); }}
                            onChange={() => togglePo(po)}
                            aria-label="Select all items in this PO"
                          />
                        )}
                      </td>
                      <td className="pt-col-line">
                        <div className="pt-po-number">{a.po_number || 'PO'}</div>
                        {a.vendor && <div className="pt-po-vendor">{a.vendor}</div>}
                        {Array.isArray(po.rooms) && po.rooms.length > 0 && (
                          <div className="pt-po-room" title={po.rooms.map(r => r.name).join(', ')}>
                            🛋 {po.rooms.map(r => r.name).join(' · ')}
                          </div>
                        )}
                        {a.status && <span className={`pt-status ${statusClass}`}>{a.status}</span>}
                      </td>
                      {FIELDS.map(f => (
                        <td key={f.key}>
                          {renderField(f, a[f.key], (v) => updatePoField(po.id, f.key, v))}
                        </td>
                      ))}
                      <td className="pt-col-files">
                        <div className="pt-attach-list">
                          {(po.attachments || []).map(att => (
                            <span key={att.id} className="pt-attach-chip">
                              <a href={att.file_url} target="_blank" rel="noopener noreferrer" title={att.name}>
                                {att.mime === 'application/pdf' ? '📄' : '🖼️'} {att.name}
                              </a>
                              <button className="pt-attach-del" onClick={() => deleteAttachment(po.id, att.id)} aria-label="Remove">×</button>
                            </span>
                          ))}
                        </div>
                        <button className="pt-attach-btn" onClick={() => triggerUpload(po.id)}>+ Attach file</button>
                      </td>
                    </tr>

                    {/* Item lines */}
                    {(po.line_items || []).map(li => (
                      <tr className="pt-item-row" key={li.id}>
                        <td className="pt-col-sel">
                          <input
                            type="checkbox"
                            checked={selectedItems.has(li.id)}
                            onChange={() => toggleItem(li.id)}
                            aria-label={`Include ${li.name || 'item'} in PDF`}
                          />
                        </td>
                        <td className="pt-col-line pt-item-name">
                          <span className="pt-item-bullet">↳</span>
                          {li.image_path ? (
                            <img src={'/' + li.image_path} alt="" className="pt-item-thumb" />
                          ) : (
                            <span className="pt-item-thumb pt-item-thumb-empty">—</span>
                          )}
                          <span className="pt-item-label">
                            {li.name || 'Item'}
                            {li.part_number ? ` · ${li.part_number}` : ''}
                            {li.quantity ? ` · Qty ${li.quantity}` : ''}
                          </span>
                        </td>
                        {FIELDS.map(f => (
                          <td key={f.key}>
                            {renderField(f, li[f.key], (v) => updateItemField(po.id, li.id, f.key, v))}
                          </td>
                        ))}
                        <td className="pt-col-files" />
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProjectTracker;
